import React, { createContext, useContext, useMemo } from 'react';

/**
 * App-wide write-capability gate.
 *
 * The *decision* (which roles may write/configure) lives in each app's auth
 * layer; this context just carries the resolved booleans down to the shared
 * primitives (`Button`, `SaveButton`, `WriteGate`) so they can disable
 * themselves for read-only users **without every call-site re-wiring
 * `disabled={!canWrite}`**.
 *
 * Default is fully permissive (`canWrite: true`, `canConfigure: true`): an app
 * that never mounts {@link WriteGuardProvider} (admin, web) — or a control
 * rendered outside it — behaves exactly as before. Only an app that opts in
 * (tenant, fed from `useAuth`) gets the read-only blocking.
 */
export type WriteCapabilityLevel = 'write' | 'config';

export interface WriteCapability {
  /** May create/update/delete business data (tenant: owner|admin|worker). */
  canWrite: boolean;
  /** May touch configuration (tenant: owner|admin). Implies `canWrite`. */
  canConfigure: boolean;
  /** Localized tooltip shown on a blocked control (e.g. "Solo lectura"). The
   *  caller owns the copy/i18n — shared primitives never hardcode a language. */
  message?: string;
}

const DEFAULT_CAPABILITY: WriteCapability = {
  canWrite: true,
  canConfigure: true,
};

const WriteGuardContext = createContext<WriteCapability>(DEFAULT_CAPABILITY);

export interface WriteGuardProviderProps {
  canWrite: boolean;
  canConfigure: boolean;
  message?: string;
  children: React.ReactNode;
}

export const WriteGuardProvider: React.FC<WriteGuardProviderProps> = ({
  canWrite,
  canConfigure,
  message,
  children,
}) => {
  const value = useMemo<WriteCapability>(
    () => ({ canWrite, canConfigure, message }),
    [canWrite, canConfigure, message],
  );
  return <WriteGuardContext.Provider value={value}>{children}</WriteGuardContext.Provider>;
};

/** Full capability snapshot (booleans + the localized blocked message). */
export const useWriteGuard = (): WriteCapability => useContext(WriteGuardContext);

/**
 * Resolve whether a control requiring `level` is permitted for the current
 * user. `'config'` controls require `canConfigure`; everything else requires
 * `canWrite`. Returns `true` when no provider is mounted (permissive default).
 */
export const useCanPerform = (level: WriteCapabilityLevel = 'write'): boolean => {
  const cap = useContext(WriteGuardContext);
  return level === 'config' ? cap.canConfigure : cap.canWrite;
};
